// `@sss/sdk` — thin client over the Simple Secret Sharing API.
//
// The boilerplate ships with a deliberately small surface: an HTTP client
// (`createClient`) and namespaced helpers per route family. Add a new
// namespace (`client.<thing>.<method>`) whenever you add a route surface
// to the API — see AGENTS.md for the recipe.
export function createClient(opts) {
    const request = async (path, init = {}) => {
        const headers = new Headers(init.headers || {});
        if (!headers.has('Content-Type') && init.body)
            headers.set('Content-Type', 'application/json');
        if (init.auth !== false) {
            const token = opts.getAccessToken ? await opts.getAccessToken() : null;
            if (token)
                headers.set('Authorization', `Bearer ${token}`);
        }
        const res = await fetch(`${opts.baseUrl}${path}`, { ...init, headers });
        if (res.status === 401 && opts.onAuthFailed)
            await opts.onAuthFailed();
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.message || `Request failed: ${res.status}`);
        }
        if (res.status === 204)
            return undefined;
        return res.json();
    };
    /** Persist the fresh pair returned by a session-revoking endpoint. */
    const withFreshTokens = async (res) => {
        if (res?.tokens && opts.onTokensRefreshed)
            await opts.onTokensRefreshed(res.tokens);
        return res;
    };
    return {
        auth: {
            register: (body) => request('/v1/auth/register', {
                method: 'POST',
                body: JSON.stringify(body),
                auth: false,
            }),
            login: (login, password) => request('/v1/auth/login', {
                method: 'POST',
                body: JSON.stringify({ login, password }),
                auth: false,
            }),
            logout: (refreshToken) => request('/v1/auth/logout', {
                method: 'POST',
                body: JSON.stringify({ refreshToken }),
                auth: false,
            }),
            refresh: (refreshToken) => request('/v1/auth/refresh-tokens', {
                method: 'POST',
                body: JSON.stringify({ refreshToken }),
                auth: false,
            }),
            forgotPassword: (email) => request('/v1/auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify({ email }),
                auth: false,
            }),
            resetPassword: (token, password) => request(`/v1/auth/reset-password?token=${encodeURIComponent(token)}`, {
                method: 'POST',
                body: JSON.stringify({ password }),
                auth: false,
            }),
            verifyEmail: (token) => request(`/v1/auth/verify-email?token=${encodeURIComponent(token)}`, {
                method: 'POST',
                auth: false,
            }),
            sendVerificationEmail: () => request('/v1/auth/send-verification-email', { method: 'POST' }),
            /** Second login step: exchange the verifyMfa token + TOTP code for tokens. */
            verifyMfa: (verifyMfaToken, mfaToken) => request('/v1/auth/verify-mfa', {
                method: 'POST',
                body: JSON.stringify({ verifyMfaToken, mfaToken }),
                auth: false,
            }),
            verifyBackupCode: (verifyMfaToken, backupCode) => request('/v1/auth/verify-backup-code', {
                method: 'POST',
                body: JSON.stringify({ verifyMfaToken, backupCode }),
                auth: false,
            }),
            /** Revokes every other session; the fresh pair is passed to `onTokensRefreshed`. */
            changePassword: (currentPassword, newPassword, mfaToken) => request('/v1/auth/change-password', {
                method: 'POST',
                body: JSON.stringify({ currentPassword, newPassword, ...(mfaToken ? { mfaToken } : {}) }),
            }).then(withFreshTokens),
            /** For OAuth-only accounts. Revokes other sessions like changePassword. */
            setPassword: (newPassword, mfaToken) => request('/v1/auth/set-password', {
                method: 'POST',
                body: JSON.stringify({ newPassword, ...(mfaToken ? { mfaToken } : {}) }),
            }).then(withFreshTokens),
            /** Start TOTP enrolment. `password` is required when the account has one. */
            enableMfa: (password) => request('/v1/auth/enable-mfa', {
                method: 'POST',
                body: JSON.stringify(password ? { password } : {}),
            }),
            /**
             * Confirm enrolment with a code from the authenticator. Enabling MFA
             * revokes other sessions; the fresh pair goes to `onTokensRefreshed`.
             */
            confirmMfa: (mfaToken) => request('/v1/auth/verify-mfa', {
                method: 'POST',
                body: JSON.stringify({ mfaToken }),
            }).then(async (res) => {
                if (res.tokens && 'access' in res.tokens)
                    await withFreshTokens({ tokens: res.tokens });
                return res;
            }),
            disableMfa: (mfaToken) => request('/v1/auth/disable-mfa', {
                method: 'POST',
                body: JSON.stringify({ mfaToken }),
            }).then(withFreshTokens),
        },
        oauth: {
            /** List of providers linked to the current user. */
            providers: () => request('/v1/auth/oauth/providers'),
            /** Unlink a provider — fails with 400 if it's the only auth method. */
            unlink: (provider) => request(`/v1/auth/oauth/${provider}/unlink`, { method: 'DELETE' }),
            /**
             * URL that starts a sign-in round-trip (navigate the browser / open an
             * auth session to it). The API redirects back to
             * `<web>/auth/callback` or `<app-scheme>://auth/callback` with
             * `?code&state` (or `?error&state`).
             */
            authorizeUrl: ({ provider, state, platform = 'web', returnUrl, codeChallenge }) => {
                const params = new URLSearchParams({ state, platform });
                if (returnUrl)
                    params.set('return_url', returnUrl);
                if (codeChallenge)
                    params.set('code_challenge', codeChallenge);
                return `${opts.baseUrl}/v1/auth/${provider}?${params.toString()}`;
            },
            /**
             * Exchange the callback code for tokens. `state` must be the one this
             * client generated; `codeVerifier` is required if a challenge was sent.
             */
            exchange: (code, state, codeVerifier) => request('/v1/auth/oauth/exchange', {
                method: 'POST',
                body: JSON.stringify({ code, state, ...(codeVerifier ? { code_verifier: codeVerifier } : {}) }),
                auth: false,
            }),
            /**
             * Start linking a provider to the signed-in account. Navigate to
             * `${baseUrl}${intent.url}`; on return the callback carries
             * `?linked=<provider>&state=<intent.state>`.
             */
            linkIntent: (provider, platform = 'web', codeChallenge) => request('/v1/auth/oauth/link-intent', {
                method: 'POST',
                body: JSON.stringify({ provider, platform, ...(codeChallenge ? { code_challenge: codeChallenge } : {}) }),
            }),
        },
        tokens: {
            /** Personal Access Tokens. Session-only — a token can't manage tokens. */
            list: () => request('/v1/tokens'),
            /** The raw key in the response is shown once; store it immediately. */
            create: (input) => request('/v1/tokens', { method: 'POST', body: JSON.stringify(input) }),
            /** Replace the scope set (complete set, not a delta). */
            updateScopes: (tokenId, scopes) => request(`/v1/tokens/${encodeURIComponent(tokenId)}`, { method: 'PATCH', body: JSON.stringify({ scopes }) }),
            revoke: (tokenId) => request(`/v1/tokens/${encodeURIComponent(tokenId)}`, { method: 'DELETE' }),
        },
        // @feature:oauthServer:start
        oauthGrants: {
            /** Connected apps (OAuth/MCP clients) the user has authorised. */
            list: () => request('/v1/oauth-grants'),
            /** Disconnect an app: revokes its grant and refresh tokens. */
            revoke: (grantId) => request(`/v1/oauth-grants/${encodeURIComponent(grantId)}`, { method: 'DELETE' }),
        },
        // @feature:oauthServer:end
        config: {
            /** Public runtime config (limits, palette, enabled OAuth providers). No auth needed. */
            get: () => request('/v1/config', { auth: false }),
        },
        users: {
            me: () => request('/v1/users'),
            /** A user's public profile (needs `profile:read` for tokens). */
            get: (userId) => request(`/v1/users/${encodeURIComponent(userId)}`),
            /** Narrow profile edit — the surface a `profile:write` token may use. */
            updateProfile: (patch) => request('/v1/users/profile', { method: 'PATCH', body: JSON.stringify(patch) }),
            update: (patch) => request('/v1/users', { method: 'PATCH', body: JSON.stringify(patch) }),
            /** Mark the current user for deletion. Revokes all sessions; client should log out. */
            deleteSelf: () => request('/v1/users', { method: 'DELETE' }),
        },
        version: {
            /** Build identifier of the running API (no auth). */
            commit: () => request('/v1/version/commit', { auth: false }),
        },
        devices: {
            list: () => request('/v1/devices'),
            register: (input) => request('/v1/devices', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            delete: (deviceId) => request(`/v1/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }),
        },
        notifications: {
            /** Defaults to the caller's own channel; other channels must be ones the caller may read. */
            list: (channels, limit = 50) => request('/v1/notifications', {
                method: 'POST',
                body: JSON.stringify({ ...(channels?.length ? { channels } : {}), limit }),
            }),
        },
    };
}
// @feature:oauthServer:end
